import React, { useRef, useCallback, useState, useMemo, useEffect, forwardRef, useImperativeHandle } from "react";
import { PitchMarkings } from "./PitchMarkings";
import { PitchObject } from "./PitchObject";
import { PitchShape } from "./PitchShape";
import { PitchText } from "./PitchText";
import { PITCH_MODES, OBJECT_TYPES, isPlayerLikeType, resolvePlayerScale } from "@/constants/pitchObjects";

const clientToSvgPoint = (svg, evt) => {
  const pt = svg.createSVGPoint();
  pt.x = evt.clientX;
  pt.y = evt.clientY;
  const ctm = svg.getScreenCTM();
  if (!ctm) return { x: 0, y: 0 };
  const p = pt.matrixTransform(ctm.inverse());
  return { x: p.x, y: p.y };
};

// Draw tools that draw into the `arrows` or `shapes` collection on pointer-up.
const DRAW_TOOLS = new Set(["arrow", "line", "rect", "circle"]);

export const Pitch = forwardRef(function Pitch(
  {
    mode,
    fieldColor,
    markingsColor,
    objects,
    arrows,
    shapes,
    texts,
    lines,
    activeTool,
    selectedId,
    selectedShapeId,
    selectedArrowId,
    selectedTextId,
    selectedLineId,
    multiSelection,
    drawStyle,
    onSelect,
    onSelectShape,
    onSelectArrow,
    onSelectText,
    onSelectLine,
    onToggleSelection,
    onMoveObject,
    onMoveShape,
    onMoveText,
    onMoveArrow,
    onMoveArrowEndpoint,
    onCurveArrow,
    onMoveLine,
    onMoveLineEndpoint,
    onResizeShape,
    onMoveShapeDim,
    onDragStart,
    onDuplicateObject,
    onDuplicateShape,
    onDuplicateText,
    onDuplicateArrow,
    onDuplicateLine,
    onAddArrow,
    onAddShape,
    onAddLine,
    onPitchClick,
    onMarqueeSelect,
    onMultiDelta,
    onMultiScale,
    onResizeObject,
    onRotateObject,
    showDimensions,
  },
  externalRef
) {
  const svgRef = useRef(null);
  useImperativeHandle(externalRef, () => ({ getSvg: () => svgRef.current }));

  // Group resize state — populated on group-handle pointer-down.
  const groupResizeRef = useRef(null);

  // Bounding box (canonical coords) of the currently multi-selected objects.
  // Only object-kind items participate in group scaling — arrows / shapes /
  // texts / lines keep their marquee-drag translation behaviour untouched.
  const groupBBox = useMemo(() => {
    if (!multiSelection || multiSelection.length < 2) return null;
    const ids = new Set(
      multiSelection.filter((m) => m.kind === "object").map((m) => m.id)
    );
    if (ids.size < 2) return null;
    const picked = objects.filter((o) => ids.has(o.id));
    if (picked.length < 2) return null;
    const xs = picked.map((o) => o.x);
    const ys = picked.map((o) => o.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    return {
      minX, maxX, minY, maxY,
      cx: (minX + maxX) / 2,
      cy: (minY + maxY) / 2,
      items: picked,
    };
  }, [multiSelection, objects]);

  const dragRef = useRef(null);
  const handleRef = useRef(null);
  const [draft, setDraft] = useState(null);
  const [marquee, setMarquee] = useState(null); // canonical {x1,y1,x2,y2}
  // Ghost placement preview — canonical {x, y} of the cursor while a
  // placement tool is active. Never enters persisted state; cleared when
  // the pointer leaves the pitch or the placement tool is cancelled.
  const [placementPreview, setPlacementPreview] = useState(null);
  const meta = PITCH_MODES[mode];

  // ------------------------------------------------------------------
  // Canonical pitch + viewport
  // ------------------------------------------------------------------
  // The activity's coordinate system is ALWAYS the full canonical pitch
  // (680 × 1050). Each view mode describes a viewport — a canonical
  // rectangle that projects onto the SVG. `canonicalToView` translates
  // canonical coords into the visible SVG coords for the active view.
  // Switching viewports never mutates any stored coordinate.
  const isVertical = meta.vertical;
  const canW = 680;
  const canH = 1050;
  const viewport = meta.viewport;
  // The SVG "view size" is the viewport size, optionally rotated 90° for
  // horizontal orientation.
  const viewW = isVertical ? viewport.w : viewport.h;
  const viewH = isVertical ? viewport.h : viewport.w;
  // Placement tool active → existing items must not intercept pointer
  // events so the click reaches the SVG background handler that spawns
  // the new object. Same treatment when a drawing tool is active so the
  // coach can draw directly on top of existing rectangles / players /
  // arrows / lines / text without them stealing the pointer.
  const placementActive = Boolean(activeTool && OBJECT_TYPES[activeTool]);
  const drawingActive = DRAW_TOOLS.has(activeTool) || activeTool === "text";
  // When the placement tool is cancelled (or replaced by a different
  // tool that is NOT a placement tool), clear the ghost so it doesn't
  // linger.
  React.useEffect(() => {
    if (!placementActive) setPlacementPreview(null);
  }, [placementActive]);  const passThroughActive = placementActive || drawingActive;
  const layerPE = passThroughActive ? "none" : undefined;

  // Canonical → viewport-relative view coords.
  const canonicalToView = useCallback(
    (x, y) => {
      const vx = x - viewport.x;
      const vy = y - viewport.y;
      // Horizontal orientation rotates the whole viewport 90° clockwise
      // so length (canonical Y) runs left→right on screen. We map
      // (vx, vy) → (viewport.h - vy, vx).
      return isVertical ? { x: vx, y: vy } : { x: viewport.h - vy, y: vx };
    },
    [isVertical, viewport.x, viewport.y, viewport.h]
  );
  const viewToCanonical = useCallback(
    (vx, vy) => {
      const localX = isVertical ? vx : vy;
      const localY = isVertical ? vy : viewport.h - vx;
      return { x: localX + viewport.x, y: localY + viewport.y };
    },
    [isVertical, viewport.x, viewport.y, viewport.h]
  );

  // Draw the full-length canonical pitch and let the SVG viewBox crop to
  // the current viewport — this ensures the pitch markings never change
  // meaning when the viewport changes.
  //   Vertical:   translate(-vp.x, -vp.y)                      → windows the pitch to the viewport rect
  //   Horizontal: translate(vp.h 0) rotate(90) translate(-vp.x -vp.y)
  //               (rotate 90° CW around origin, then shift so the rotated
  //                viewport aligns with (0, 0)…(vp.h, vp.w))
  const innerTransform = isVertical
    ? `translate(${-viewport.x} ${-viewport.y})`
    : `translate(${viewport.h} 0) rotate(90) translate(${-viewport.x} ${-viewport.y})`;

  const inMulti = (kind, id) =>
    (multiSelection || []).some((m) => m.kind === kind && m.id === id);
  const othersOf = (kind, id) =>
    (multiSelection || []).filter((m) => !(m.kind === kind && m.id === id));

  const handlePointerDownObject = (e, id) => {
    const svg = svgRef.current;
    const p = clientToSvgPoint(svg, e);
    const c = viewToCanonical(p.x, p.y);
    const obj = objects.find((o) => o.id === id);
    if (!obj) return;
    // Shift-click toggles this item in the multi-selection; no drag.
    if (e.shiftKey && onToggleSelection) {
      onToggleSelection("object", id);
      return;
    }
    // Preserve existing multi-selection when clicking one of its members
    // so the group can be moved together; otherwise single-select.
    const isMulti = inMulti("object", id);
    if (!isMulti && onSelect) onSelect(id);
    let dragId = id;
    if (onDragStart) onDragStart(id);
    if (e.altKey && onDuplicateObject) {
      const newId = onDuplicateObject(id);
      if (newId) dragId = newId;
    }
    dragRef.current = {
      kind: "object",
      id: dragId,
      offX: c.x - obj.x,
      offY: c.y - obj.y,
      pointer0: c,
      pointerId: e.pointerId,
      multiOthers: isMulti ? othersOf("object", id) : null,
    };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (_) {
      // noop
    }
  };

  const handleDimPointerDown = (e, id) => {
    const svg = svgRef.current;
    const p = clientToSvgPoint(svg, e);
    const c = viewToCanonical(p.x, p.y);
    const sh = shapes.find((s) => s.id === id);
    if (!sh) return;
    // Ensure a single undo reverses the whole drag.
    if (onDragStart) onDragStart(id);
    const cur = sh.dimOffset || { dx: 0, dy: 0 };
    dragRef.current = {
      kind: "shapeDim",
      id,
      pointer0: c,
      dim0: { dx: cur.dx || 0, dy: cur.dy || 0 },
      pointerId: e.pointerId,
    };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (_) {
      // noop
    }
  };

  const handlePointerDownShape = (e, id) => {
    const svg = svgRef.current;
    const p = clientToSvgPoint(svg, e);
    const c = viewToCanonical(p.x, p.y);
    const sh = shapes.find((s) => s.id === id);
    if (!sh) return;
    if (e.shiftKey && onToggleSelection) {
      onToggleSelection("shape", id);
      return;
    }
    if (onDragStart) onDragStart(id);
    let dragId = id;
    const isMulti = inMulti("shape", id);
    if (e.altKey && onDuplicateShape) {
      const newId = onDuplicateShape(id);
      if (newId) {
        dragId = newId;
        onSelectShape(newId);
      }
    } else if (!isMulti) {
      onSelectShape(id);
    }
    const anchor = sh.type === "rect" ? { x: sh.x, y: sh.y } : { x: sh.cx, y: sh.cy };
    dragRef.current = {
      kind: "shape",
      id: dragId,
      offX: c.x - anchor.x,
      offY: c.y - anchor.y,
      pointer0: c,
      pointerId: e.pointerId,
      multiOthers: isMulti ? othersOf("shape", id) : null,
    };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (_) {
      // noop
    }
  };

  const handlePointerDownText = (e, id) => {
    const svg = svgRef.current;
    const p = clientToSvgPoint(svg, e);
    const c = viewToCanonical(p.x, p.y);
    const t = texts.find((x) => x.id === id);
    if (!t) return;
    if (e.shiftKey && onToggleSelection) {
      onToggleSelection("text", id);
      return;
    }
    if (onDragStart) onDragStart(id);
    let dragId = id;
    const isMulti = inMulti("text", id);
    if (e.altKey && onDuplicateText) {
      const newId = onDuplicateText(id);
      if (newId) {
        dragId = newId;
        onSelectText(newId);
      }
    } else if (!isMulti) {
      onSelectText(id);
    }
    dragRef.current = {
      kind: "text",
      id: dragId,
      offX: c.x - t.x,
      offY: c.y - t.y,
      pointer0: c,
      pointerId: e.pointerId,
      multiOthers: isMulti ? othersOf("text", id) : null,
    };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (_) {
      // noop
    }
  };

  const handleShapeHandlePointerDown = (e, hMode, id) => {
    const svg = svgRef.current;
    const sh = shapes.find((s) => s.id === id);
    if (!sh) return;
    handleRef.current = {
      mode: hMode,
      kind: "shape",
      id,
      shape0: sh,
      pointerId: e.pointerId,
    };
    if (onDragStart) onDragStart(id);
    try {
      svg.setPointerCapture(e.pointerId);
    } catch (_) {
      // noop
    }
  };

  const handlePointerDownArrow = (e, id) => {
    e.stopPropagation();
    const svg = svgRef.current;
    const p = clientToSvgPoint(svg, e);
    const c = viewToCanonical(p.x, p.y);
    const arr = arrows.find((a) => a.id === id);
    if (!arr) return;
    if (e.shiftKey && onToggleSelection) {
      onToggleSelection("arrow", id);
      return;
    }
    if (onDragStart) onDragStart(id);
    let dragId = id;
    const isMulti = inMulti("arrow", id);
    if (e.altKey && onDuplicateArrow) {
      const newId = onDuplicateArrow(id);
      if (newId) {
        dragId = newId;
        onSelectArrow(newId);
      }
    } else if (!isMulti) {
      onSelectArrow(id);
    }
    dragRef.current = {
      kind: "arrow",
      id: dragId,
      pointer0: c,
      arrow0: {
        x1: arr.x1,
        y1: arr.y1,
        x2: arr.x2,
        y2: arr.y2,
        cx: typeof arr.cx === "number" ? arr.cx : null,
        cy: typeof arr.cy === "number" ? arr.cy : null,
      },
      pointerId: e.pointerId,
      multiOthers: isMulti ? othersOf("arrow", id) : null,
    };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (_) {
      // noop
    }
  };

  const handleArrowEndpointDown = (e, id, which) => {
    const svg = svgRef.current;
    e.stopPropagation();
    handleRef.current = {
      mode: "arrowEndpoint",
      kind: "arrowEndpoint",
      id,
      which,
      pointerId: e.pointerId,
    };
    if (onDragStart) onDragStart(id);
    try {
      svg.setPointerCapture(e.pointerId);
    } catch (_) {
      // noop
    }
  };

  const handleArrowCurveDown = (e, id) => {
    e.stopPropagation();
    const svg = svgRef.current;
    const p = clientToSvgPoint(svg, e);
    const c = viewToCanonical(p.x, p.y);
    const arr = arrows.find((a) => a.id === id);
    if (!arr) return;
    const midX = (arr.x1 + arr.x2) / 2;
    const midY = (arr.y1 + arr.y2) / 2;
    const curCx = typeof arr.cx === "number" ? arr.cx : midX;
    const curCy = typeof arr.cy === "number" ? arr.cy : midY;
    // Visual midpoint of a quadratic Bezier at t=0.5: 0.5*mid + 0.5*C
    const handleX = 0.5 * midX + 0.5 * curCx;
    const handleY = 0.5 * midY + 0.5 * curCy;
    handleRef.current = {
      mode: "arrowCurve",
      kind: "arrowCurve",
      id,
      offX: c.x - handleX,
      offY: c.y - handleY,
      midX,
      midY,
      pointerId: e.pointerId,
    };
    if (onDragStart) onDragStart(id);
    try {
      svg.setPointerCapture(e.pointerId);
    } catch (_) {
      // noop
    }
  };

  const handlePointerDownLine = (e, id) => {
    e.stopPropagation();
    const svg = svgRef.current;
    const p = clientToSvgPoint(svg, e);
    const c = viewToCanonical(p.x, p.y);
    const ln = (lines || []).find((l) => l.id === id);
    if (!ln) return;
    if (e.shiftKey && onToggleSelection) {
      onToggleSelection("line", id);
      return;
    }
    if (onDragStart) onDragStart(id);
    let dragId = id;
    const isMulti = inMulti("line", id);
    if (e.altKey && onDuplicateLine) {
      const newId = onDuplicateLine(id);
      if (newId) {
        dragId = newId;
        onSelectLine && onSelectLine(newId);
      }
    } else if (!isMulti) {
      onSelectLine && onSelectLine(id);
    }
    dragRef.current = {
      kind: "line",
      id: dragId,
      pointer0: c,
      line0: { x1: ln.x1, y1: ln.y1, x2: ln.x2, y2: ln.y2 },
      pointerId: e.pointerId,
      multiOthers: isMulti ? othersOf("line", id) : null,
    };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (_) {
      // noop
    }
  };

  const handleLineEndpointDown = (e, id, which) => {
    e.stopPropagation();
    const svg = svgRef.current;
    handleRef.current = {
      mode: "lineEndpoint",
      kind: "lineEndpoint",
      id,
      which,
      pointerId: e.pointerId,
    };
    if (onDragStart) onDragStart(id);
    try {
      svg.setPointerCapture(e.pointerId);
    } catch (_) {
      // noop
    }
  };

  // ---- Group resize (multi-select) ----
  // Grab the group handle → capture the pointer's distance from the group
  // centre AND each selected object's starting position + scale, so we can
  // scale positions AND sizes proportionally on move.
  const handleGroupResizeDown = (e) => {
    if (!groupBBox) return;
    e.stopPropagation();
    e.preventDefault();
    const svg = svgRef.current;
    const p = clientToSvgPoint(svg, e);
    const centerView = canonicalToView(groupBBox.cx, groupBBox.cy);
    const initialDist = Math.hypot(p.x - centerView.x, p.y - centerView.y);
    groupResizeRef.current = {
      centerCanonical: { x: groupBBox.cx, y: groupBBox.cy },
      centerView,
      initialDist: Math.max(1, initialDist),
      // Snapshot each targeted object's starting (x, y, scale). Sizes are
      // captured as NUMERIC scales so the resulting group transform stays
      // consistent whether the coach previously chose an S/M/L preset or
      // dragged an individual handle.
      targets: groupBBox.items.map((o) => ({
        id: o.id,
        x0: o.x,
        y0: o.y,
        scale0: isPlayerLikeType(o.type)
          ? resolvePlayerScale(o.size)
          : (typeof o.size === "number" ? o.size : 1),
      })),
      pointerId: e.pointerId,
    };
    if (onDragStart) onDragStart(null);
    try {
      svg.setPointerCapture(e.pointerId);
    } catch (_) {
      // noop
    }
  };

  const handlePointerMove = (e) => {
    const svg = svgRef.current;
    const p = clientToSvgPoint(svg, e);
    const c = viewToCanonical(p.x, p.y);

    // Ghost placement preview: only update while a placement tool is
    // active and the pointer is over the pitch. The preview never
    // enters persisted state — it's temporary render-only UI.
    if (placementActive && !handleRef.current && !groupResizeRef.current && !draft && !marquee) {
      setPlacementPreview({ x: c.x, y: c.y });
    }

    // Group resize takes priority over individual object handles.
    if (groupResizeRef.current) {
      const g = groupResizeRef.current;
      const dist = Math.hypot(p.x - g.centerView.x, p.y - g.centerView.y);
      const raw = dist / g.initialDist;
      const factor = Math.max(0.35, Math.min(3, raw));
      if (onMultiScale) onMultiScale(factor, g.centerCanonical, g.targets);
      return;
    }

    if (handleRef.current) {
      if (handleRef.current.mode === "arrowEndpoint") {
        onMoveArrowEndpoint(handleRef.current.id, handleRef.current.which, c.x, c.y);
        return;
      }
      if (handleRef.current.mode === "arrowCurve") {
        // Solve the quadratic Bezier control point so the visual midpoint
        // (t=0.5 = 0.5*midEndpoints + 0.5*C) sits at the cursor position,
        // honouring the grab offset captured at pointer-down.
        const { id, offX, offY, midX, midY } = handleRef.current;
        const handleX = c.x - offX;
        const handleY = c.y - offY;
        const newCx = 2 * handleX - midX;
        const newCy = 2 * handleY - midY;
        if (onCurveArrow) onCurveArrow(id, newCx, newCy);
        return;
      }
      if (handleRef.current.mode === "lineEndpoint") {
        onMoveLineEndpoint(handleRef.current.id, handleRef.current.which, c.x, c.y);
        return;
      }
      if (handleRef.current.kind === "shape") {
        const { id, shape0, mode: hMode } = handleRef.current;
        if (shape0.type === "rect") {
          // Compute new (x, y, w, h) based on which handle is being
          // dragged. The opposite corner/edge stays anchored. Minimum
          // rectangle size is 10 canonical units to avoid degenerate
          // shapes.
          const MIN = 10;
          const left0 = shape0.x;
          const top0 = shape0.y;
          const right0 = shape0.x + shape0.w;
          const bottom0 = shape0.y + shape0.h;
          let nx = left0;
          let ny = top0;
          let nw = shape0.w;
          let nh = shape0.h;
          const setLeft = () => {
            nx = Math.min(c.x, right0 - MIN);
            nw = right0 - nx;
          };
          const setRight = () => {
            nw = Math.max(MIN, c.x - left0);
          };
          const setTop = () => {
            ny = Math.min(c.y, bottom0 - MIN);
            nh = bottom0 - ny;
          };
          const setBottom = () => {
            nh = Math.max(MIN, c.y - top0);
          };
          switch (hMode) {
            case "resize-nw": setLeft(); setTop(); break;
            case "resize-n":  setTop(); break;
            case "resize-ne": setRight(); setTop(); break;
            case "resize-e":  setRight(); break;
            case "resize-se": setRight(); setBottom(); break;
            case "resize-s":  setBottom(); break;
            case "resize-sw": setLeft(); setBottom(); break;
            case "resize-w":  setLeft(); break;
            default:          setRight(); setBottom(); break; // legacy single handle
          }
          onResizeShape(id, { x: nx, y: ny, w: nw, h: nh });
        } else if (shape0.type === "circle") {
          const nr = Math.max(6, Math.hypot(c.x - shape0.cx, c.y - shape0.cy));
          onResizeShape(id, { r: nr });
        }
        return;
      }
      const { mode: hMode, objView, initialDist, initialScale } = handleRef.current;
      const dx = p.x - objView.x;
      const dy = p.y - objView.y;
      const dist = Math.hypot(dx, dy);
      if (hMode === "resize") {
        // Smooth delta-based resize: newScale = initialScale × (dist / initialDist).
        // No visual jump on initial pointer-down; player centre stays fixed.
        const raw = initialScale * (dist / initialDist);
        const newSize = Math.max(0.35, Math.min(3, raw));
        onResizeObject(newSize);
      } else if (hMode === "rotate") {
        const ang = (Math.atan2(dy, dx) * 180) / Math.PI + 90;
        onRotateObject(ang);
      }
      return;
    }

    if (dragRef.current) {
      const dref = dragRef.current;
      const { kind, id, offX, offY, pointer0, arrow0, line0, multiOthers } = dref;
      // Incremental delta since the last pointermove — used for multi-select
      // group translation so the "others" don't compound cumulative deltas.
      const last = dref.lastPointer || pointer0;
      const idx = c.x - last.x;
      const idy = c.y - last.y;
      dref.lastPointer = c;
      if (kind === "arrow") {
        // Body drag always translates the whole arrow, preserving length,
        // angle and curvature. The dedicated curve handle is the only way
        // to adjust curvature; endpoint handles remain independent.
        const dx = c.x - pointer0.x;
        const dy = c.y - pointer0.y;
        const patch = {
          x1: arrow0.x1 + dx,
          y1: arrow0.y1 + dy,
          x2: arrow0.x2 + dx,
          y2: arrow0.y2 + dy,
        };
        if (typeof arrow0.cx === "number" && typeof arrow0.cy === "number") {
          patch.cx = arrow0.cx + dx;
          patch.cy = arrow0.cy + dy;
        }
        onMoveArrow(id, patch);
        if (multiOthers && multiOthers.length > 0 && onMultiDelta) {
          onMultiDelta(idx, idy, multiOthers);
        }
        return;
      }
      if (kind === "line") {
        const dx = c.x - pointer0.x;
        const dy = c.y - pointer0.y;
        onMoveLine(id, {
          x1: line0.x1 + dx,
          y1: line0.y1 + dy,
          x2: line0.x2 + dx,
          y2: line0.y2 + dy,
        });
        if (multiOthers && onMultiDelta) onMultiDelta(idx, idy, multiOthers);
        return;
      }
      if (kind === "shapeDim") {
        // Move only the annotation's offset — never the shape.
        const dx = c.x - pointer0.x;
        const dy = c.y - pointer0.y;
        if (onMoveShapeDim) {
          onMoveShapeDim(id, {
            dx: (dref.dim0?.dx || 0) + dx,
            dy: (dref.dim0?.dy || 0) + dy,
          });
        }
        return;
      }
      // No canonical-bound clamp — objects are allowed to move OFF the
      // pitch surface. The SVG viewBox has extra padding around the pitch
      // so off-field positions remain visible and selectable.
      const nx = c.x - offX;
      const ny = c.y - offY;
      if (kind === "shape") {
        onMoveShape(id, nx, ny);
      } else if (kind === "text") {
        onMoveText(id, nx, ny);
      } else {
        onMoveObject(id, nx, ny);
      }
      if (multiOthers && onMultiDelta && pointer0) {
        onMultiDelta(idx, idy, multiOthers);
      }
      return;
    }
    if (marquee) {
      setMarquee({ ...marquee, x2: c.x, y2: c.y });
      return;
    }
    if (draft) setDraft({ ...draft, x2: c.x, y2: c.y });
  };

  const handlePointerUp = (e) => {
    if (groupResizeRef.current) {
      try {
        e.currentTarget.releasePointerCapture(groupResizeRef.current.pointerId);
      } catch (_) {
        // noop
      }
      groupResizeRef.current = null;
      return;
    }
    if (handleRef.current) {
      try {
        e.currentTarget.releasePointerCapture(handleRef.current.pointerId);
      } catch (_) {
        // noop
      }
      handleRef.current = null;
      return;
    }
    if (dragRef.current) {
      try {
        e.currentTarget.releasePointerCapture(dragRef.current.pointerId);
      } catch (_) {
        // noop
      }
      dragRef.current = null;
      return;
    }
    if (marquee) {
      const box = marquee;
      const dx = Math.abs(box.x2 - box.x1);
      const dy = Math.abs(box.y2 - box.y1);
      if (dx > 6 && dy > 6 && onMarqueeSelect) {
        onMarqueeSelect(box);
      } else if (onMarqueeSelect) {
        // A plain click on empty pitch (no drag) — clear any current
        // selection so the user can dismiss the selected item by clicking
        // away, even while the Select tool is active.
        onMarqueeSelect({ x1: box.x1, y1: box.y1, x2: box.x1, y2: box.y1 });
      }
      setMarquee(null);
      return;
    }
    if (!draft) return;
    const { tool, x1, y1, x2, y2 } = draft;
    const dx = x2 - x1;
    const dy = y2 - y1;
    const dist = Math.hypot(dx, dy);
    if (dist > 8) {
      const style = drawStyle || { color: "#0F172A", dashed: false };
      if (tool === "arrow") {
        onAddArrow({ x1, y1, x2, y2, color: style.color, dashed: style.dashed });
      } else if (tool === "line") {
        onAddLine && onAddLine({ x1, y1, x2, y2, color: style.color, dashed: style.dashed });
      } else if (tool === "rect") {
        onAddShape({
          type: "rect",
          x: Math.min(x1, x2),
          y: Math.min(y1, y2),
          w: Math.abs(dx),
          h: Math.abs(dy),
          color: style.color,
          dashed: style.dashed,
        });
      } else if (tool === "circle") {
        onAddShape({ type: "circle", cx: x1, cy: y1, r: dist, color: style.color, dashed: style.dashed });
      }
    }
    setDraft(null);
  };

  const handleHandlePointerDown = (e, hMode, id) => {
    const svg = svgRef.current;
    const obj = objects.find((o) => o.id === id);
    if (!obj) return;
    const def = OBJECT_TYPES[obj.type];
    const objView = canonicalToView(obj.x, obj.y);
    // Capture the pointer's initial distance from the object centre + the
    // object's current scale so resize can proceed as a smooth delta:
    //   newScale = initialScale × (currentDist / initialDist)
    // — the size doesn't jump the moment you touch the handle.
    const p = clientToSvgPoint(svg, e);
    const initialDist = Math.hypot(p.x - objView.x, p.y - objView.y);
    const isPlayer = isPlayerLikeType(obj.type);
    const initialScale = isPlayer
      ? resolvePlayerScale(obj.size)
      : (typeof obj.size === "number" ? obj.size : 1);
    handleRef.current = {
      mode: hMode,
      id,
      objView,
      baseSize: def.size,
      initialDist: Math.max(1, initialDist),
      initialScale,
      pointerId: e.pointerId,
    };
    if (onDragStart) onDragStart(id);
    try {
      svg.setPointerCapture(e.pointerId);
    } catch (_) {
      // noop
    }
  };

  const handleBackgroundPointerDown = (e) => {
    const svg = svgRef.current;
    const p = clientToSvgPoint(svg, e);
    const c = viewToCanonical(p.x, p.y);

    if (DRAW_TOOLS.has(activeTool)) {
      setDraft({ tool: activeTool, x1: c.x, y1: c.y, x2: c.x, y2: c.y });
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch (_) {
        // noop
      }
      return;
    }
    if (activeTool === "select") {
      setMarquee({ x1: c.x, y1: c.y, x2: c.x, y2: c.y });
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch (_) {
        // noop
      }
      return;
    }
    if (onPitchClick) onPitchClick(c.x, c.y);
    else onSelect(null);
  };

  const arrowToView = (a) => {
    const p1 = canonicalToView(a.x1, a.y1);
    const p2 = canonicalToView(a.x2, a.y2);
    const hasCurve = typeof a.cx === "number" && typeof a.cy === "number";
    const pc = hasCurve ? canonicalToView(a.cx, a.cy) : null;
    return {
      ...a,
      vx1: p1.x,
      vy1: p1.y,
      vx2: p2.x,
      vy2: p2.y,
      hasCurve,
      vcx: pc ? pc.x : null,
      vcy: pc ? pc.y : null,
    };
  };

  // Build the SVG `d` attribute for an arrow — a straight line unless the
  // arrow has a curve control point, in which case it's a quadratic Bezier.
  const arrowPathD = (v) =>
    v.hasCurve
      ? `M ${v.vx1} ${v.vy1} Q ${v.vcx} ${v.vcy} ${v.vx2} ${v.vy2}`
      : `M ${v.vx1} ${v.vy1} L ${v.vx2} ${v.vy2}`;

  // ------------------------------------------------------------------
  // Global render order
  // ------------------------------------------------------------------
  // Items across all collections share a single visual stacking order.
  // Priority for the render sort:
  //   1) explicit `z` on the item (set by Bring Forward / Send Backward /
  //      To Front / To Back — coach-controlled global stacking)
  //   2) fallback ordering based on the item's kind + insertion position
  //      so freshly-created items and legacy sessions without stored `z`
  //      render in a sensible default order.
  //
  // We keep the underlying collections intact — this is purely a render
  // concern.
  const KIND_BASE = { shape: 0, arrow: 1, line: 2, text: 3, object: 4 };
  const sortedItems = useMemo(() => {
    const entries = [];
    (shapes || []).forEach((s, i) => entries.push({ kind: "shape", item: s, ins: i }));
    (arrows || []).forEach((a, i) => entries.push({ kind: "arrow", item: a, ins: i }));
    (lines || []).forEach((l, i) => entries.push({ kind: "line", item: l, ins: i }));
    (texts || []).forEach((t, i) => entries.push({ kind: "text", item: t, ins: i }));
    (objects || []).forEach((o, i) => entries.push({ kind: "object", item: o, ins: i }));
    const effZ = (e) =>
      typeof e.item.z === "number"
        ? e.item.z
        : (KIND_BASE[e.kind] || 0) * 1_000_000 + e.ins;
    return entries
      .map((e) => ({ ...e, z: effZ(e) }))
      .sort((a, b) => a.z - b.z);
  }, [shapes, arrows, lines, texts, objects]);

  // Renderers per item kind — extracted so the sorted map stays terse.
  //
  // Pitch rotation applied by innerTransform for horizontal views: 90° CW.
  // Any element inside the innerTransform group that shows readable text
  // (dimension labels, PitchText content) applies an *inverse* rotation
  // so the reader still sees upright glyphs. Purely geometric shapes
  // (rectangles, circles, arrows, lines) rotate with the pitch — that is
  // the desired behaviour so a "goal-line-parallel" rectangle stays
  // goal-line-parallel after rotation.
  const pitchRotation = isVertical ? 0 : 90;
  const renderShape = (sh) => (
    <g key={`shape-${sh.id}`} transform={innerTransform}>
      <PitchShape
        shape={sh}
        isSelected={sh.id === selectedShapeId}
        showHandles={activeTool === "select" && sh.id === selectedShapeId}
        showDimensions={showDimensions}
        pitchRotation={pitchRotation}
        onPointerDown={handlePointerDownShape}
        onHandlePointerDown={handleShapeHandlePointerDown}
        onDimPointerDown={handleDimPointerDown}
      />
    </g>
  );

  const renderText = (t) => (
    <g key={`text-${t.id}`} transform={innerTransform}>
      <PitchText
        text={t}
        isSelected={t.id === selectedTextId}
        pitchRotation={pitchRotation}
        onPointerDown={handlePointerDownText}
      />
    </g>
  );

  const renderArrow = (a) => {
    const v = arrowToView(a);
    const isSel = a.id === selectedArrowId;
    const isMultiSel = (multiSelection || []).some((m) => m.kind === "arrow" && m.id === a.id);
    const strokeCol = a.color || "#0F172A";
    const dashArr = a.dashed ? "10 6" : undefined;
    const showEndpoints = activeTool === "select" && isSel;
    const d = arrowPathD(v);
    return (
      <g key={`arrow-${a.id}`}>
        <path
          d={d}
          fill="none"
          stroke={strokeCol}
          strokeWidth={isSel || isMultiSel ? 5 : 3}
          strokeLinecap="round"
          strokeDasharray={dashArr}
          markerEnd="url(#arrowhead)"
          style={{ cursor: "move", pointerEvents: passThroughActive ? "none" : "stroke" }}
          data-testid={`arrow-${a.id}`}
          onPointerDown={(e) => handlePointerDownArrow(e, a.id)}
        />
        {showEndpoints && (
          <>
            <circle
              cx={v.vx1}
              cy={v.vy1}
              r={7}
              fill="#10B981"
              stroke="#FFFFFF"
              strokeWidth={2}
              style={{ cursor: "grab" }}
              data-testid={`arrow-endpoint-start-${a.id}`}
              onPointerDown={(e) => handleArrowEndpointDown(e, a.id, "start")}
            />
            <circle
              cx={v.vx2}
              cy={v.vy2}
              r={7}
              fill="#10B981"
              stroke="#FFFFFF"
              strokeWidth={2}
              style={{ cursor: "grab" }}
              data-testid={`arrow-endpoint-end-${a.id}`}
              onPointerDown={(e) => handleArrowEndpointDown(e, a.id, "end")}
            />
            {(() => {
              const midX = (v.vx1 + v.vx2) / 2;
              const midY = (v.vy1 + v.vy2) / 2;
              const hx = v.hasCurve ? 0.5 * midX + 0.5 * v.vcx : midX;
              const hy = v.hasCurve ? 0.5 * midY + 0.5 * v.vcy : midY;
              return (
                <circle
                  cx={hx}
                  cy={hy}
                  r={6}
                  fill="#FFFFFF"
                  stroke="#F97316"
                  strokeWidth={2.5}
                  style={{ cursor: "grab" }}
                  data-testid={`arrow-curve-handle-${a.id}`}
                  onPointerDown={(e) => handleArrowCurveDown(e, a.id)}
                />
              );
            })()}
          </>
        )}
        {isMultiSel && !isSel && (
          <>
            <circle cx={v.vx1} cy={v.vy1} r={4} fill="#10B981" pointerEvents="none" />
            <circle cx={v.vx2} cy={v.vy2} r={4} fill="#10B981" pointerEvents="none" />
          </>
        )}
      </g>
    );
  };

  const renderLine = (l) => {
    const p1 = canonicalToView(l.x1, l.y1);
    const p2 = canonicalToView(l.x2, l.y2);
    const isSel = l.id === selectedLineId;
    const isMultiSel = (multiSelection || []).some((m) => m.kind === "line" && m.id === l.id);
    const strokeCol = l.color || "#0F172A";
    const dashArr = l.dashed ? "10 6" : undefined;
    const showEndpoints = activeTool === "select" && isSel;
    return (
      <g key={`line-${l.id}`}>
        <line
          x1={p1.x}
          y1={p1.y}
          x2={p2.x}
          y2={p2.y}
          stroke={strokeCol}
          strokeWidth={isSel || isMultiSel ? 5 : 3}
          strokeLinecap="round"
          strokeDasharray={dashArr}
          data-testid={`line-${l.id}`}
          onPointerDown={(e) => handlePointerDownLine(e, l.id)}
          style={{ cursor: "move", pointerEvents: passThroughActive ? "none" : "stroke" }}
        />
        {showEndpoints && (
          <>
            <circle
              cx={p1.x}
              cy={p1.y}
              r={7}
              fill="#10B981"
              stroke="#FFFFFF"
              strokeWidth={2}
              style={{ cursor: "grab" }}
              data-testid={`line-endpoint-start-${l.id}`}
              onPointerDown={(e) => handleLineEndpointDown(e, l.id, "start")}
            />
            <circle
              cx={p2.x}
              cy={p2.y}
              r={7}
              fill="#10B981"
              stroke="#FFFFFF"
              strokeWidth={2}
              style={{ cursor: "grab" }}
              data-testid={`line-endpoint-end-${l.id}`}
              onPointerDown={(e) => handleLineEndpointDown(e, l.id, "end")}
            />
          </>
        )}
        {isMultiSel && !isSel && (
          <>
            <circle cx={p1.x} cy={p1.y} r={4} fill="#10B981" pointerEvents="none" />
            <circle cx={p2.x} cy={p2.y} r={4} fill="#10B981" pointerEvents="none" />
          </>
        )}
      </g>
    );
  };

  const renderObject = (obj) => {
    const v = canonicalToView(obj.x, obj.y);
    const showHandles = activeTool === "select" && obj.id === selectedId;
    const isMultiSel = (multiSelection || []).some((m) => m.kind === "object" && m.id === obj.id);
    return (
      <g key={`object-${obj.id}`}>
        <PitchObject
          obj={{ ...obj, x: v.x, y: v.y }}
          isSelected={obj.id === selectedId}
          showHandles={showHandles}
          allowResize={true}
          pitchRotation={pitchRotation}
          onPointerDown={handlePointerDownObject}
          onSelect={onSelect}
          onHandlePointerDown={handleHandlePointerDown}
        />
        {isMultiSel && obj.id !== selectedId && (
          <circle
            cx={v.x}
            cy={v.y}
            r={22}
            fill="none"
            stroke="#10B981"
            strokeWidth={2}
            strokeDasharray="4 3"
            pointerEvents="none"
          />
        )}
      </g>
    );
  };

  const renderItem = (entry) => {
    if (entry.kind === "shape") return renderShape(entry.item);
    if (entry.kind === "text") return renderText(entry.item);
    if (entry.kind === "arrow") return renderArrow(entry.item);
    if (entry.kind === "line") return renderLine(entry.item);
    if (entry.kind === "object") return renderObject(entry.item);
    return null;
  };

  // Shapes stored in canonical coords too. For horizontal orientation we
  // can't just translate; instead we wrap all shapes in the same rotation
  // group as pitch markings so their geometry matches. That's fine because
  // rectangles/circles don't have labels.
  // Padding around the pitch (in view units) so objects living off the
  // canonical pitch surface remain visible and reachable. Aligned to the
  // viewport dimensions so half/third views retain a proportional margin.
  const OFF_FIELD_PAD = 60;
  const svgViewBox = `${-OFF_FIELD_PAD} ${-OFF_FIELD_PAD} ${viewW + 2 * OFF_FIELD_PAD} ${viewH + 2 * OFF_FIELD_PAD}`;

  return (
    <div className="w-full h-full flex items-center justify-center">
      <svg
        ref={svgRef}
        viewBox={svgViewBox}
        preserveAspectRatio="xMidYMid meet"
        className="h-full w-full max-h-full max-w-full drop-shadow-sm"
        data-testid="pitch-canvas"
        onPointerDown={handleBackgroundPointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onPointerLeave={() => setPlacementPreview(null)}
        style={{
          touchAction: "none",
          cursor:
            activeTool === "arrow" ||
            activeTool === "line" ||
            activeTool === "rect" ||
            activeTool === "circle" ||
            activeTool === "text"
              ? "crosshair"
              : undefined,
        }}
      >
        <defs>
          <marker
            id="arrowhead"
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#0F172A" />
          </marker>
        </defs>

        <rect x={0} y={0} width={viewW} height={viewH} fill={fieldColor || "#86BC5A"} rx={4} />

        {/* Pitch markings always drawn in canonical space — the SVG
           viewBox + inner viewport translation crop to the active view. */}
        <g transform={innerTransform}>
          <PitchMarkings half={false} stroke={markingsColor || "#FFFFFF"} />
        </g>

        {/* All user items rendered in a single sorted pass so cross-type
           Bring Forward / Send Backward reliably re-stacks them. */}
        <g style={{ pointerEvents: layerPE }}>
          {sortedItems.map((entry) => renderItem(entry))}
        </g>

        {/* Ghost placement preview — faint version of the object about to
            be placed. Follows the cursor while a placement tool is active
            and the pointer is over the pitch. Never enters persisted state.
            Rendered above committed objects so the coach can clearly see
            where the new object will land, but at low opacity so existing
            items remain readable. */}
        {placementActive && placementPreview && !draft && (() => {
          const type = activeTool;
          const def = OBJECT_TYPES[type];
          if (!def) return null;
          const isPlayerLike = isPlayerLikeType(type);
          // Mirror `addObjectOfType` defaults exactly so the ghost is a
          // faithful preview of what will actually be placed.
          let previewLabel = "";
          if (type === "goalkeeper" || type === "coach") {
            previewLabel = def.shortLabel;
          }
          // Player / Defender / Neutral / Goalkeeper default to blank
          // display mode on placement; Coach keeps its "C".
          const previewDisplayMode =
            isPlayerLike && type !== "coach" ? "blank" : undefined;
          const previewSize =
            type === "goal" || type === "miniGoal"
              ? 1.5
              : isPlayerLike
                ? "medium"
                : 1;
          const stubObj = {
            id: "__preview__",
            type,
            x: placementPreview.x,
            y: placementPreview.y,
            label: previewLabel,
            size: previewSize,
            rotation: 0,
            displayMode: previewDisplayMode,
          };
          // CRITICAL: PitchObject expects x/y in VIEW coordinates (that's
          // how `renderObject` passes them for every committed object).
          // Canonical placementPreview.x/y must be converted the same way
          // or the ghost drifts from the cursor in cropped / horizontal
          // views. This mirrors `renderObject` above exactly.
          const previewView = canonicalToView(stubObj.x, stubObj.y);
          return (
            <g
              data-testid="placement-preview"
              style={{ opacity: 0.4, pointerEvents: "none" }}
            >
              <PitchObject
                obj={{ ...stubObj, x: previewView.x, y: previewView.y }}
                isSelected={false}
                showHandles={false}
                allowResize={false}
                pitchRotation={pitchRotation}
              />
            </g>
          );
        })()}

        {/* Draft shape / arrow being drawn — always drawn above committed items. */}
        {draft && (() => {
          const drawColor = (drawStyle && drawStyle.color) || "#0F172A";
          const draftDash = drawStyle && drawStyle.dashed ? "10 6" : "6 4";
          if (draft.tool === "arrow") {
            const p1 = canonicalToView(draft.x1, draft.y1);
            const p2 = canonicalToView(draft.x2, draft.y2);
            return (
              <line
                x1={p1.x}
                y1={p1.y}
                x2={p2.x}
                y2={p2.y}
                stroke={drawColor}
                strokeWidth={3}
                strokeDasharray={draftDash}
                strokeLinecap="round"
                markerEnd="url(#arrowhead)"
              />
            );
          }
          if (draft.tool === "line") {
            const p1 = canonicalToView(draft.x1, draft.y1);
            const p2 = canonicalToView(draft.x2, draft.y2);
            return (
              <line
                x1={p1.x}
                y1={p1.y}
                x2={p2.x}
                y2={p2.y}
                stroke={drawColor}
                strokeWidth={3}
                strokeDasharray={draftDash}
                strokeLinecap="round"
              />
            );
          }
          if (draft.tool === "rect") {
            const p1 = canonicalToView(draft.x1, draft.y1);
            const p2 = canonicalToView(draft.x2, draft.y2);
            return (
              <rect
                x={Math.min(p1.x, p2.x)}
                y={Math.min(p1.y, p2.y)}
                width={Math.abs(p1.x - p2.x)}
                height={Math.abs(p1.y - p2.y)}
                stroke={drawColor}
                strokeWidth={3}
                strokeDasharray={draftDash}
                fill="rgba(15,23,42,0.05)"
              />
            );
          }
          if (draft.tool === "circle") {
            const c = canonicalToView(draft.x1, draft.y1);
            const p2 = canonicalToView(draft.x2, draft.y2);
            const r = Math.hypot(c.x - p2.x, c.y - p2.y);
            return (
              <circle
                cx={c.x}
                cy={c.y}
                r={r}
                stroke={drawColor}
                strokeWidth={3}
                strokeDasharray={draftDash}
                fill="rgba(15,23,42,0.05)"
              />
            );
          }
          return null;
        })()}

        {/* Group bounding box + resize handle for multi-select */}
        {groupBBox && (() => {
          const tl = canonicalToView(groupBBox.minX, groupBBox.minY);
          const br = canonicalToView(groupBBox.maxX, groupBBox.maxY);
          const x = Math.min(tl.x, br.x);
          const y = Math.min(tl.y, br.y);
          const w = Math.abs(br.x - tl.x);
          const h = Math.abs(br.y - tl.y);
          const pad = 22;
          const handleR = 8;
          const handleX = x + w + pad;
          const handleY = y + h + pad;
          return (
            <g data-testid="group-resize" pointerEvents="none">
              <rect
                x={x - pad}
                y={y - pad}
                width={w + pad * 2}
                height={h + pad * 2}
                fill="none"
                stroke="#10B981"
                strokeWidth={1.25}
                strokeDasharray="6 4"
                rx={6}
                data-testid="group-resize-bbox"
              />
              <line
                x1={x + w + pad * 0.35}
                y1={y + h + pad * 0.35}
                x2={handleX - 4}
                y2={handleY - 4}
                stroke="#10B981"
                strokeWidth={1.25}
                strokeDasharray="3 3"
              />
              <circle
                cx={handleX}
                cy={handleY}
                r={handleR}
                fill="#10B981"
                stroke="#ffffff"
                strokeWidth={2}
                style={{ cursor: "nwse-resize", pointerEvents: "auto" }}
                onPointerDown={handleGroupResizeDown}
                data-testid="group-resize-handle"
              />
            </g>
          );
        })()}

        {/* Marquee selection rectangle */}
        {marquee && (() => {
          const p1 = canonicalToView(marquee.x1, marquee.y1);
          const p2 = canonicalToView(marquee.x2, marquee.y2);
          return (
            <rect
              x={Math.min(p1.x, p2.x)}
              y={Math.min(p1.y, p2.y)}
              width={Math.abs(p1.x - p2.x)}
              height={Math.abs(p1.y - p2.y)}
              fill="rgba(16, 185, 129, 0.08)"
              stroke="#10B981"
              strokeWidth={1.5}
              strokeDasharray="6 4"
              pointerEvents="none"
              data-testid="marquee-box"
            />
          );
        })()}
      </svg>
    </div>
  );
});
